// src/web/lib/hooks/useAssets.ts

import type {
	AssetFile,
	AssetListItem,
	AssetListResponse,
	AssetSearchParams,
	AssetWithDetails,
} from '@shared/types'
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useCallback } from 'react'
import { useFilterParams } from '@/stores/filters'
import { api, handleApiError } from '../api'
import { createAssetInfiniteQueryKey, createAssetQueryKey, queryConfig, queryKeys } from '../query'

// Re-export types for consumers
// Asset is an alias for AssetListItem (used in list views)
// AssetWithDetails is the full type (used in detail views)
export type Asset = AssetListItem
export type { AssetFile, AssetListItem, AssetListResponse, AssetSearchParams, AssetWithDetails }

// Hook to fetch paginated assets with filters
export function useAssets(params?: AssetSearchParams, enabled: boolean = true) {
	const filterParams = useFilterParams()
	const finalParams = params ?? filterParams

	return useQuery({
		queryKey: createAssetQueryKey(
			finalParams.query,
			finalParams.folder,
			finalParams.fieldFilters,
			finalParams.lifecycleState,
			finalParams.fileType,
			finalParams.sortBy,
			finalParams.sortOrder,
			finalParams.page,
			finalParams.pageSize
		),
		queryFn: async () => {
			const response = await api.api.assets.get({
				query: {
					query: finalParams.query,
					folder: finalParams.folder,
					fieldFilters: finalParams.fieldFilters,
					lifecycleState: finalParams.lifecycleState,
					fileType: finalParams.fileType,
					ids: (finalParams as AssetSearchParams).ids,
					page: finalParams.page,
					pageSize: finalParams.pageSize,
					sortBy: finalParams.sortBy,
					sortOrder: finalParams.sortOrder,
				},
			})

			if (response.error) {
				throw new Error(handleApiError(response.error))
			}

			return response.data as AssetListResponse
		},
		enabled,
		...queryConfig.dynamic, // Assets are dynamic data
	})
}

// Hook for infinite scroll pagination
export function useInfiniteAssets(params?: Omit<AssetSearchParams, 'page'>) {
	return useInfiniteQuery({
		queryKey: createAssetInfiniteQueryKey(
			params?.query,
			params?.folder,
			params?.fieldFilters,
			params?.lifecycleState,
			params?.fileType,
			params?.sortBy,
			params?.sortOrder
		),
		queryFn: async ({ pageParam = 1 }) => {
			const response = await api.api.assets.get({
				query: {
					query: params?.query,
					folder: params?.folder,
					fieldFilters: params?.fieldFilters,
					lifecycleState: params?.lifecycleState,
					fileType: params?.fileType,
					page: pageParam,
					pageSize: params?.pageSize ?? 18,
					sortBy: params?.sortBy,
					sortOrder: params?.sortOrder,
				},
			})

			if (response.error) {
				throw new Error(handleApiError(response.error))
			}

			return response.data as AssetListResponse
		},
		initialPageParam: 1,
		getNextPageParam: (lastPage) => {
			if (lastPage.page < lastPage.totalPages) {
				return lastPage.page + 1
			}
			return undefined
		},
		enabled: !!params?.query, // Only fetch when there's a search query
	})
}

// Hook to fetch a single asset by ID
export function useAsset(id: string | undefined) {
	return useQuery({
		queryKey: queryKeys.assets.detail(id ?? ''),
		queryFn: async () => {
			if (!id) throw new Error('Asset ID is required')

			const response = await api.api.assets({ id }).get()

			if (response.error) {
				throw new Error(handleApiError(response.error))
			}

			return response.data as AssetWithDetails
		},
		enabled: !!id,
		...queryConfig.user, // Asset details are user-specific
	})
}

// Helper to get file type category
export function getFileTypeCategory(asset: Asset): string {
	const mediaType = asset.file?.mediaTypeType?.toLowerCase()
	const mediaSubtype = asset.file?.mediaTypeSubtype?.toLowerCase()
	const fileType = asset.file?.fileType?.toLowerCase()

	if (mediaType === 'image' || fileType?.match(/\.(jpg|jpeg|png|gif|webp|svg|bmp|ico)$/i)) {
		return 'image'
	}
	if (mediaType === 'video' || fileType?.match(/\.(mp4|mov|avi|mkv|webm|wmv)$/i)) {
		return 'video'
	}
	if (mediaType === 'audio' || fileType?.match(/\.(mp3|wav|ogg|flac|aac|m4a)$/i)) {
		return 'audio'
	}
	if (
		mediaType === 'application' &&
		mediaSubtype &&
		(mediaSubtype === 'pdf' ||
			mediaSubtype.includes('msword') ||
			mediaSubtype.includes('wordprocessing') ||
			mediaSubtype.includes('ms-excel') ||
			mediaSubtype.includes('spreadsheet') ||
			mediaSubtype.includes('ms-powerpoint') ||
			mediaSubtype.includes('presentation') ||
			mediaSubtype === 'rtf' ||
			mediaSubtype === 'csv')
	) {
		return 'document'
	}
	if (mediaType === 'text') {
		return 'document'
	}
	if (fileType?.match(/\.(pdf|doc|docx|xls|xlsx|ppt|pptx|txt|rtf|csv|html)$/i)) {
		return 'document'
	}
	return 'other'
}

// Asset file with version info for history
export interface AssetFileVersion {
	id: string
	assetId: string
	filename: string
	size: number
	checksum: string | null
	dateAdded: string | null
	fileType: string | null
	mediaTypeType: string | null
	mediaTypeSubtype: string | null
	width: number | null
	height: number | null
	duration: number | null
	orientation: string | null
	usesTransparency: boolean
	version: number
	isCurrent: boolean
}

export interface AssetHistoryResponse {
	assetId: string
	currentFileId: string | null
	files: AssetFileVersion[]
	total: number
}

// Hook to fetch asset file history
export function useAssetHistory(assetId: string | undefined) {
	return useQuery({
		queryKey: queryKeys.assets.history(assetId ?? ''),
		queryFn: async () => {
			if (!assetId) throw new Error('Asset ID is required')

			const response = await api.api.assets({ id: assetId }).history.get()

			if (response.error) {
				throw new Error(handleApiError(response.error))
			}

			return response.data as AssetHistoryResponse
		},
		enabled: !!assetId,
		...queryConfig.user, // Asset history is user-specific
	})
}

// Hook to delete an asset
export function useDeleteAsset() {
	const queryClient = useQueryClient()

	return useMutation({
		mutationFn: async (assetId: string) => {
			const response = await api.api.assets({ id: assetId }).delete()

			if (response.error) {
				throw new Error(handleApiError(response.error))
			}

			return response.data
		},
		onSuccess: () => {
			// Invalidate all asset lists and stats
			queryClient.invalidateQueries({ queryKey: queryKeys.assets.all })
			// Asset deletes move to Trash — refresh the trash view too
			queryClient.invalidateQueries({ queryKey: ['trash'] })
			queryClient.invalidateQueries({ queryKey: queryKeys.activity.all })
		},
	})
}

// Hook to prefetch assets for a folder (used for hover prefetching)
export function usePrefetchAssets() {
	const queryClient = useQueryClient()

	return useCallback(
		(folderId: string) => {
			queryClient.prefetchQuery({
				queryKey: createAssetQueryKey(
					undefined, // query
					folderId, // folder
					undefined, // fieldFilters
					undefined, // lifecycleState
					undefined, // fileType
					'dateStaged', // sortBy
					'desc', // sortOrder
					1, // page
					18 // pageSize
				),
				queryFn: async () => {
					const response = await api.api.assets.get({
						query: {
							folder: folderId,
							page: 1,
							pageSize: 18,
							sortBy: 'dateStaged',
							sortOrder: 'desc',
						},
					})

					if (response.error) {
						throw new Error(handleApiError(response.error))
					}

					return response.data as AssetListResponse
				},
				staleTime: 1000 * 60 * 5, // 5 minutes
			})
		},
		[queryClient]
	)
}
